import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as xlsx from 'xlsx';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
// import CheckCircle from '../../../../assets/allInvoice-assets/check-circle.svg';
import trash from '../../../../assets/allInvoice-assets/trash-2.svg';
import upload from '../../../../assets/allInvoice-assets/upload.svg';
// import file from '../../../../assets/allInvoice-assets/file.svg';
import { ReactComponent as QuickBook } from '../../../../assets/common-assets/quickBook.svg';
import { ReactComponent as Xero } from '../../../../assets/common-assets/xero.svg';
import { ReactComponent as Zoho } from '../../../../assets/common-assets/zoho.svg';
import { ReactComponent as Odoo } from '../../../../assets/common-assets/odoo.svg';
import { ReactComponent as Tally } from '../../../../assets/common-assets/tally.svg';
import { ReactComponent as Pdf } from '../../../../assets/common-assets/pdf.svg';
import { ReactComponent as Xml } from '../../../../assets/common-assets/xml.svg';
import { ReactComponent as Json } from '../../../../assets/common-assets/json.svg';
import { ReactComponent as Excel } from '../../../../assets/common-assets/excelIcon.svg';

import {
  ExportDocumentListItem,
  ExportDocumentPopover,
  classes,
} from './utils';
import AddTag from './AddTag';
import {
  deleteDocument,
  exportJSONFromGridAction,
  exportXMLFromGridAction,
  exportXLSXFromGridAction,
} from '../../../../services/allInvoiceApi';
import CustomTooltip from '../../utils/CustomTooltip';
import ConfirmDialog from '../../utils/ConfirmDialog';
import Toast from '../../utils/Toast';
import { DOCUMENT_ACTION_TOOLTIP } from '../../../../constants/tooltipConstant';
import { lightPalette } from '../../../../theme';
import generatePdf from './generatePdf';
import { exportQB } from '../../../../constants/staticContent';
import ExportAuthPopUp from './ExportAuthPopUp';
import OdooForm from '../flow-configuration/OdooForm';
import TallyPrimeForm from '../flow-configuration/TallyPrimeForm';
import MODELS from '../../../../constants/models';

const Action = ({
  id,
  documentName,
  documentTag,
  onApiCompletion,
  onTagApiCompletion,
  validatedAt,
  // status,
  result,
  documentType,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [documentExportDialogOpen, setDocumentExportDialogOpen] =
    useState(false);
  const [open, setOpen] = useState(false);
  const [exportType, setExportType] = useState('');
  const isManual = true;
  const [odooFormManual, setOdooFormManual] = useState(false);
  const [tallyPrimeExport, setTallyPrimeExport] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const askingMessage = `Are You Sure You Want to Delete This Document : ${documentName} ?`;

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
    setExportType('');
  };

  const handleConfirmAction = async () => {
    if (id !== null) {
      const response = await deleteDocument({ id });
      if (response) {
        onApiCompletion(id);
      }
    }

    handleCloseDialog();
  };

  const handleOpenExportDialog = (event) => {
    setAnchorEl(event.currentTarget);
    setDocumentExportDialogOpen(true);
  };

  const handleCloseExportDialog = () => {
    setAnchorEl(null);
    setDocumentExportDialogOpen(false);
  };

  const handleExportToJSON = async () => {
    try {
      if (!validatedAt) {
        Toast({ message: exportQB.invoiceNotValidated, variant: 'warning' });
      } else if (id !== null) {
        setIsLoading(true);
        const exportResponse = await exportJSONFromGridAction(id);
        const jsonString = JSON.stringify(exportResponse.data.data.jsonData);
        const { fileName } = exportResponse.data.data;
        const blob = new Blob([jsonString], {
          type: 'text/plain;charset=utf-8',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      return error;
    }
    handleCloseExportDialog();
  };

  const handleExportToXML = async () => {
    try {
      if (!validatedAt) {
        Toast({ message: exportQB.invoiceNotValidated, variant: 'warning' });
      } else {
        setIsLoading(true);
        const fileData = await exportXMLFromGridAction(id);
        const { xmlData } = fileData;
        const { fileName } = fileData;
        if (xmlData) {
          const blob = new Blob([xmlData], {
            type: 'application/xml; charset=utf-8',
          });

          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = fileName;
          link.click();

          URL.revokeObjectURL(link.href);
          setIsLoading(false);
        }
      }
    } catch (error) {
      setIsLoading(false);
      return error;
    }
    handleCloseExportDialog();
  };

  const handleExportToXLSX = async () => {
    try {
      if (!validatedAt) {
        Toast({ message: exportQB.invoiceNotValidated, variant: 'warning' });
      } else {
        setIsLoading(true);
        const fileData = await exportXLSXFromGridAction(id);
        const { data, fileName } = fileData;

        if (data) {
          const worksheet = xlsx.utils.json_to_sheet(data);

          const workbook = xlsx.utils.book_new();
          xlsx.utils.book_append_sheet(workbook, worksheet, 'Data');

          const xlsxData = xlsx.write(workbook, {
            type: 'buffer',
            bookType: 'xlsx',
          });

          const blob = new Blob([xlsxData], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });

          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = fileName;
          link.click();

          URL.revokeObjectURL(link.href);
          setIsLoading(false);
        }
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error exporting XLSX:', error);
    }
    handleCloseExportDialog();
  };

  const handleExportToTallyPrime = async () => {
    if (!validatedAt) {
      Toast({ message: exportQB.invoiceNotValidated, variant: 'warning' });
    } else {
      setExportType('TallyPrime');
      setAnchorEl(null);
      setDocumentExportDialogOpen(false);
      setOpen(true);
    }
  };

  const handleExportToQuickBook = async () => {
    if (!validatedAt) {
      Toast({ message: exportQB.invoiceNotValidated, variant: 'warning' });
    } else {
      setExportType('QuickBook');
      setAnchorEl(null);
      setDocumentExportDialogOpen(false);
      setOpen(true);
    }
  };

  const handleExportToXero = () => {
    if (!validatedAt) {
      Toast({ message: exportQB.invoiceNotValidated, variant: 'warning' });
    } else {
      setExportType('Xero');
      setAnchorEl(null);
      setDocumentExportDialogOpen(false);
      setOpen(true);
    }
  };

  const handleExportToZoho = () => {
    if (!validatedAt) {
      Toast({ message: exportQB.invoiceNotValidated, variant: 'warning' });
    } else {
      setExportType('Zoho');
      setAnchorEl(null);
      setDocumentExportDialogOpen(false);
      setOpen(true);
    }
  };

  const closeOdooFormManual = (_, reason) => {
    if (reason !== 'backdropClick') {
      setOdooFormManual(false);
    }
  };
  const closeTallyPrime = (_, reason) => {
    if (reason !== 'backdropClick') {
      setTallyPrimeExport(false);
    }
  };
  const handleOdooNewAuthManualAndExport = () => {
    setOdooFormManual(false);
    setOpen(false);
    setExportType('');
    setAnchorEl(null);
    setDocumentExportDialogOpen(false);
  };
  const handleNewAndExport = () => {
    setTallyPrimeExport(false);
    setOpen(false);
    setExportType('');
    setAnchorEl(null);
    setDocumentExportDialogOpen(false);
  };
  const handleExportToOdoo = () => {
    if (!validatedAt) {
      Toast({ message: exportQB.invoiceNotValidated, variant: 'warning' });
    } else {
      setExportType('Odoo');
      setOpen(true);
    }
  };

  const ExportTypeDocument = [
    {
      itemText: 'Export to JSON',
      function: handleExportToJSON,
      icon: <Json className={classes.logoStyle} />,
    },
    {
      itemText: 'Export to XML',
      function: handleExportToXML,
      icon: <Xml className={classes.logoStyle} />,
    },
    {
      itemText: 'Export to Excel',
      function: handleExportToXLSX,
      icon: <Excel className={classes.logoStyle} />,
    },
    {
      itemText: 'Export to PDF',
      function: () => {
        if (!validatedAt) {
          Toast({ message: exportQB.invoiceNotValidated, variant: 'warning' });
        } else {
          generatePdf(result, documentName, documentType, id);
        }
      },
      icon: <Pdf className={classes.logoStyle} />,
    },
    {
      itemText: 'Export to Quickbooks',
      function: handleExportToQuickBook,
      icon: <QuickBook className={classes.logoStyle} />,
    },
    {
      itemText: 'Export to Xero',
      function: handleExportToXero,
      icon: <Xero className={classes.logoStyle} />,
    },
    {
      itemText: 'Export to Zoho',
      function: handleExportToZoho,
      icon: <Zoho className={classes.logoStyle} />,
    },
    {
      itemText: 'Export to Odoo',
      function: handleExportToOdoo,
      icon: <Odoo className={classes.logoStyle} />,
    },
    {
      itemText: 'Export to Tally Prime',
      function: handleExportToTallyPrime,
      icon: <Tally className={classes.logoStyle} />,
    },
  ];

  const documentPathsModel = {
    [MODELS.INVOICE]: 'invoices',
    [MODELS.SALES]: 'sales',
    [MODELS.BANK_STATEMENT]: 'bank-statements',
    [MODELS.CREDIT_CARD_STATEMENT]: 'creditcardstatements',
    [MODELS.RECEIPT]: 'receipts',
  };

  const location = useLocation();
  const isSalesOrPurchase =
    location.pathname.includes('/sales') ||
    location.pathname.includes('/invoices') ||
    location.pathname.includes('/receipts');
  const ExportOptions = isSalesOrPurchase
    ? ExportTypeDocument.filter(
        (option) => option.itemText !== 'Export to Excel',
      )
    : ExportTypeDocument.filter(
        (option) =>
          option.itemText === 'Export to Excel' ||
          option.itemText === 'Export to XML' ||
          option.itemText === 'Export to JSON',
      );

  const documentPaths = documentPathsModel[documentType] || 'receipts';

  // const getDocumentTypeForPath = (value) => {
  //   switch (value) {
  //     case 'BS':
  //       return 'bank-statement';
  //     case 'CC':
  //       return 'creditcardstatement';
  //     case 'sales':
  //       return 'sale';
  //     case 'invoice':
  //       return 'invoice';
  //     case 'receipt':
  //       return 'receipt';
  //     default: return '';
  //   }
  // };

  return (
    <Box className={classes.actionBox}>
      {isLoading ? (
        <Box className={classes.loaderBox}>
          <CircularProgress />
        </Box>
      ) : null}
      <Link to={`/${documentPaths}/${id}`}>
        <AddTag
          id={id}
          documentTag={documentTag}
          upgradeGrid={onTagApiCompletion}
          onApiCompletion={onApiCompletion}
          documentType={documentType}
        />
      </Link>
      {/* {status === 'Completed' ? (
        <Link to={`/${getDocumentTypeForPath(documentType)}/${id}`}>
          <Box className="my-icon">
            <CustomTooltip title={DOCUMENT_ACTION_TOOLTIP.VALIDATE}>
              <Box
                component="img"
                src={CheckCircle}
                alt="check-circle"
                className={classes.actionIcons}
                style={{
                  height: '15px',
                  width: '15px',
                }}
              />
            </CustomTooltip>
          </Box>
        </Link>
      ) : null} */}
      <Link to={`/${documentPaths}/${id}`}>
        <Box className={documentExportDialogOpen ? 'action' : 'my-icon'}>
          <CustomTooltip title={DOCUMENT_ACTION_TOOLTIP.EXPORT}>
            <Box
              component="img"
              src={upload}
              alt="Export"
              className={classes.actionIcons}
              onClick={handleOpenExportDialog}
              style={{
                height: '15px',
                width: '15px',
              }}
            />
          </CustomTooltip>
          <ExportDocumentPopover
            open={documentExportDialogOpen}
            anchorEl={anchorEl}
            onClose={handleCloseExportDialog}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <List>
              {ExportOptions?.map((element) => (
                <ExportDocumentListItem
                  disablePadding
                  key={element.itemText}
                  onClick={element.function}
                >
                  <ListItemButton>
                    <ListItemIcon>
                      {/* <Box component="img" src={element.icon} alt="export file" /> */}
                      {element.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle1"
                          color={lightPalette.shoeWax.darker}
                          fontWeight={500}
                          ml={2}
                        >
                          {element.itemText}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ExportDocumentListItem>
              ))}
            </List>
          </ExportDocumentPopover>
        </Box>
      </Link>
      <Link to={`/${documentPaths}/${id}`}>
        <Box className="my-icon">
          <CustomTooltip title={DOCUMENT_ACTION_TOOLTIP.DELETE}>
            <Box
              component="img"
              src={trash}
              alt="trash"
              className={classes.actionIcons}
              onClick={() => handleOpenDialog()}
              style={{
                height: '15px',
                width: '15px',
              }}
            />
          </CustomTooltip>
        </Box>
        {isDialogOpen ? (
          <ConfirmDialog
            open={isDialogOpen}
            onClose={handleCloseDialog}
            onConfirm={handleConfirmAction}
            askingMessage={askingMessage}
          />
        ) : null}
      </Link>
      <OdooForm
        odooForm={odooFormManual}
        closeOdooForm={closeOdooFormManual}
        handleOdooNewAuthManualAndExport={handleOdooNewAuthManualAndExport}
        isManual={isManual}
      />
      <TallyPrimeForm
        tallyForm={tallyPrimeExport}
        closeTallyForm={closeTallyPrime}
        handleNewAndExport={handleNewAndExport}
        isManual={isManual}
      />
      <ExportAuthPopUp
        open={open}
        handleClose={handleClose}
        exportType={exportType}
        invoiceId={id}
        setOdooFormManual={setOdooFormManual}
        setTallyPrime={setTallyPrimeExport}
        documentType={documentType}
      />
    </Box>
  );
};

Action.defaultProps = {
  id: null,
  documentName: null,
  // status: undefined,
  documentType: null,
  validatedAt: null,
};

Action.propTypes = {
  id: PropTypes.string,
  validatedAt: PropTypes.string,
  documentName: PropTypes.string,
  documentTag: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      tag: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onApiCompletion: PropTypes.func.isRequired,
  onTagApiCompletion: PropTypes.func.isRequired,
  // status: PropTypes.string,
  result: PropTypes.node.isRequired,
  documentType: PropTypes.string,
};

export default Action;
